import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Space, Row, Col, Card, Carousel, Avatar } from 'antd';
import {
  SafetyCertificateOutlined, ExperimentOutlined, RobotOutlined,
  MedicineBoxOutlined, CalculatorOutlined, ThunderboltOutlined,
  LineChartOutlined, FileTextOutlined, BookOutlined, ApiOutlined,
  ArrowRightOutlined, CheckCircleOutlined, TeamOutlined,
  GlobalOutlined, LoginOutlined, UserAddOutlined,
  LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { siteAPI } from '../services/api';
import PageFooter from '../components/PageFooter';
import { markdownToHtml } from '../utils/markdown';
import { isExternalHref, resolvePublicAssetUrl, unwrapSiteConfigValue } from '../utils/siteConfig';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { useResponsive } from '../contexts/ResponsiveContext';

const { Title, Text, Paragraph } = Typography;

/* ── Icon mapping helper ── */
const iconMap: Record<string, React.ReactNode> = {
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  RobotOutlined: <RobotOutlined />,
  ExperimentOutlined: <ExperimentOutlined />,
  TeamOutlined: <TeamOutlined />,
  CalculatorOutlined: <CalculatorOutlined />,
  LineChartOutlined: <LineChartOutlined />,
  MedicineBoxOutlined: <MedicineBoxOutlined />,
  ThunderboltOutlined: <ThunderboltOutlined />,
  GlobalOutlined: <GlobalOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  ApiOutlined: <ApiOutlined />,
  BookOutlined: <BookOutlined />,
};

/* Legacy icon name → new Ant Design name mapping */
const legacyIconMap: Record<string, string> = {
  safety: 'SafetyCertificateOutlined',
  robot: 'RobotOutlined',
  experiment: 'ExperimentOutlined',
  team: 'TeamOutlined',
  calculator: 'CalculatorOutlined',
  'line-chart': 'LineChartOutlined',
  'medicine-box': 'MedicineBoxOutlined',
  thunderbolt: 'ThunderboltOutlined',
  global: 'GlobalOutlined',
  'file-text': 'FileTextOutlined',
  api: 'ApiOutlined',
  book: 'BookOutlined',
};

const getIcon = (iconName?: string, size = 24) => {
  if (!iconName) return null;
  const resolved = iconMap[iconName] || iconMap[legacyIconMap[iconName] || ''];
  return resolved ? React.cloneElement(resolved as React.ReactElement, { style: { fontSize: size, color: GOLD } }) : null;
};

const getSortedItems = <T extends { sort_order?: number }>(items?: T[]): T[] => {
  return [...(items || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

const NAVIGATION_LABELS: Record<string, string> = {
  Features: '核心能力',
  'How It Works': '工作流程',
  Resources: '资源中心',
};

export const HeroTitleContent: React.FC<{ title: string }> = ({ title }) => {
  const normalizedTitle = (title || '').replace(/让给药化/g, '让给药更');
  const lines = normalizedTitle.replace(/\\n/g, '\n').split('\n');

  return (
    <>
      {lines.map((line, lineIdx) => (
        <div key={lineIdx} className="landing-hero-title-line">
          {line.split(/(\*\*.*?\*\*)/).map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
              ? <span key={i} style={{ color: GOLD }}>{part.slice(2, -2)}</span>
              : <span key={i}>{part}</span>
          )}
        </div>
      ))}
    </>
  );
};

/* ── Color constants ── */
const NAVY = '#0B2545';
const NAVY_MID = '#13315C';
const NAVY_LIGHT = '#1B4175';
const GOLD = '#C5A55A';
const GOLD_LIGHT = '#F5EFE0';
const OFF_WHITE = '#F8F9FC';

/* ── Section wrapper ── */
const Section: React.FC<{
  id?: string; bg?: string; py?: number; className?: string; children: React.ReactNode;
}> = ({ id, bg = '#fff', py = 72, className, children }) => (
  <section id={id} className={`landing-section ${className || ''}`.trim()} style={{ background: bg, padding: `${py}px 0` }}>
    <div className="landing-section-inner">
      {children}
    </div>
  </section>
);

/* ── Section title ── */
const SectionTitle: React.FC<{
  title: string; subtitle?: string; light?: boolean;
}> = ({ title, subtitle, light }) => (
  <div className={`landing-section-title ${light ? 'is-light' : ''}`}>
    <div className="landing-section-title-rule" />
    <Title level={2} style={{
      color: light ? '#fff' : NAVY, fontSize: 34, marginBottom: 12, fontWeight: 700, letterSpacing: -0.5,
    }}>{title}</Title>
    {subtitle && (
      <Text style={{ color: light ? 'rgba(255,255,255,0.76)' : '#5A6577', fontSize: 16, letterSpacing: 0.2 }}>
        {subtitle}
      </Text>
    )}
  </div>
);

/* ══════════════════════════════════════════════════════════════
   Hero Carousel — 背景图轮播 + 磨砂蒙版 + 每张图对应不同文字
   无幻灯片时显示渐变背景 fallback
   ══════════════════════════════════════════════════════════════ */
const HERO_FLOATING_STYLES = `
.landing-hero-stage {
  position: relative;
  min-height: 520px;
  display: grid;
  grid-template-columns: minmax(0, 0.86fr) minmax(520px, 1.14fr);
  gap: 64px;
  align-items: center;
}

.landing-hero-visual-shell {
  position: relative;
  width: 100%;
  min-height: 468px;
  margin-left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  isolation: isolate;
}

.landing-hero-visual-shell::before {
  display: none;
}

.landing-hero-visual-shell::after {
  display: none;
}

.landing-hero-visual-frame {
  position: relative;
  z-index: 1;
  width: min(100%, 748px);
  height: 382px;
  min-height: 382px;
  margin: 0;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.07) 42%, rgba(255,255,255,0.035) 100%),
    rgba(12,39,72,0.34);
  border: 1px solid rgba(255,255,255,0.14);
  box-shadow:
    0 28px 68px rgba(3,17,34,0.24),
    0 1px 0 rgba(255,255,255,0.12),
    inset 0 1px 0 rgba(255,255,255,0.24);
  backdrop-filter: blur(10px);
}

.landing-hero-visual-frame::before {
  content: '';
  position: absolute;
  inset: 10px 10px auto;
  z-index: 4;
  height: 34px;
  border-radius: 22px 22px 12px 12px;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.055) 100%);
  box-shadow: inset 0 -1px 0 rgba(255,255,255,0.08);
  pointer-events: none;
}

.landing-hero-visual-frame::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: inherit;
  background:
    linear-gradient(90deg, rgba(11,37,69,0.18) 0%, rgba(11,37,69,0.035) 20%, rgba(11,37,69,0) 58%, rgba(11,37,69,0.12) 100%),
    linear-gradient(180deg, rgba(11,37,69,0.18) 0%, rgba(11,37,69,0) 30%, rgba(11,37,69,0.16) 100%),
    radial-gradient(ellipse at 50% 102%, rgba(197,165,90,0.1), rgba(197,165,90,0) 38%);
  pointer-events: none;
}

.landing-hero-visual-media-wrap {
  position: relative;
  z-index: 1;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  margin: 0;
  border-radius: 24px;
  overflow: hidden;
  background:
    linear-gradient(180deg, #F4FAFD 0%, #DCEBF4 100%);
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.6),
    inset 0 -48px 82px rgba(11,37,69,0.1);
}

.landing-hero-visual-media-wrap::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(90deg, rgba(244,250,253,0.46) 0%, rgba(244,250,253,0) 12%, rgba(244,250,253,0) 88%, rgba(244,250,253,0.32) 100%),
    linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 26%, rgba(11,37,69,0.1) 100%);
  pointer-events: none;
}

.landing-hero-visual-media-wrap::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: inherit;
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.34),
    inset 0 0 38px rgba(255,255,255,0.18);
  pointer-events: none;
}

.landing-hero-visual-plate {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  opacity: 0.96;
  filter: saturate(0.92) contrast(1.02) brightness(1.015);
  mix-blend-mode: normal;
  transform: scale(1.012);
  transform-origin: center center;
}

.landing-hero-copy-shell {
  position: relative;
  left: auto;
  top: auto;
  transform: none;
  width: 100%;
  max-width: 560px;
  z-index: 2;
}

.landing-hero-copy-shell::before {
  content: '';
  position: absolute;
  inset: -32px -54px -32px -22px;
  border-radius: 999px;
  background:
    radial-gradient(ellipse at 34% 50%, rgba(11,37,69,0.98) 0%, rgba(11,37,69,0.9) 46%, rgba(11,37,69,0.28) 76%, rgba(11,37,69,0) 100%);
  filter: blur(20px);
  z-index: -1;
}

.landing-hero-copy-inner {
  padding: 6px 12px 6px 0;
}

.landing-hero-copy-inner .landing-hero-subtitle,
.landing-hero-copy-inner .landing-hero-subtitle a,
.landing-hero-copy-inner .landing-hero-subtitle p {
  color: rgba(255,255,255,0.82) !important;
}

.landing-hero-copy-inner .landing-hero-title {
  margin-top: 12px !important;
}

.landing-hero-copy-inner .landing-hero-actions {
  margin-top: 24px;
}

.landing-hero-fallback-visual {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  min-height: 100%;
  margin: 0;
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 58% 42%, rgba(197,165,90,0.16), rgba(197,165,90,0) 30%),
    radial-gradient(circle at 46% 54%, rgba(73,169,255,0.14), rgba(73,169,255,0) 34%),
    linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
}

.landing-hero-fallback-card {
  text-align: center;
  padding: 0;
  border-radius: 0;
  background: transparent;
  border: none;
  color: #FFFFFF;
  backdrop-filter: none;
  transform: none;
}

.landing-hero-fallback-card .anticon {
  font-size: 80px;
  color: #C5A55A;
}

.landing-hero-fallback-label {
  margin-top: 18px;
  color: rgba(255,255,255,0.68);
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.landing-loading-line {
  height: 14px;
  margin-bottom: 16px;
  border-radius: 999px;
  background: rgba(255,255,255,0.13);
}

.landing-loading-line.is-kicker {
  width: 42%;
  height: 10px;
  margin-bottom: 26px;
}

.landing-loading-line.is-title {
  width: 92%;
  height: 44px;
  margin-bottom: 12px;
}

.landing-loading-line.is-copy {
  width: 84%;
  height: 14px;
  margin-top: 24px;
  margin-bottom: 12px;
}

.landing-loading-line.is-short {
  width: 62%;
  margin-top: 0;
}

@media (min-width: 769px) and (max-width: 991px) {
  .landing-hero-stage {
    min-height: auto;
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .landing-hero-copy-shell {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    width: min(100%, 620px);
    margin-bottom: 28px;
  }

  .landing-hero-copy-shell::before {
    inset: -20px;
    background: linear-gradient(135deg, rgba(11,37,69,0.84) 0%, rgba(11,37,69,0.7) 100%);
    filter: blur(12px);
  }

  .landing-hero-copy-inner {
    padding-right: 12px;
  }

  .landing-hero-visual-shell,
  .landing-hero-visual-frame,
  .landing-hero-visual-media-wrap,
  .landing-hero-fallback-visual {
    width: 100%;
    min-height: 300px;
  }

  .landing-hero-visual-shell::before {
    inset: 34px 8px 28px 20px;
    border-radius: 30px;
    filter: blur(24px);
  }

  .landing-hero-visual-shell::after {
    inset: auto 8% 24px 8%;
    height: 54px;
    filter: blur(24px);
  }

  .landing-hero-visual-frame {
    width: 100%;
    height: 300px;
    min-height: 300px;
    margin: 10px 0 16px;
    border-radius: 28px;
  }

  .landing-hero-visual-frame::before {
    inset: 0;
    border-radius: inherit;
  }

  .landing-hero-visual-media-wrap,
  .landing-hero-fallback-visual {
    width: calc(100% - 16px);
    height: calc(100% - 16px);
    min-height: calc(100% - 16px);
    border-radius: 22px;
  }

  .landing-hero-visual-plate {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    transform: scale(1.05);
  }

  .landing-hero-fallback-card {
    padding: 0;
  }
}

@media (max-width: 768px) {
  .landing-hero-section {
    padding: 18px 0 42px !important;
  }

  .landing-hero-arrow {
    display: none;
  }

  .landing-page .hero-dots {
    bottom: 16px;
  }

  .landing-hero-mobile-shell {
    position: relative;
    max-width: 380px;
    margin: 0 auto;
    padding: 0 8px;
  }

  .landing-hero-mobile-shell.is-text-only {
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
    box-sizing: border-box;
    padding: 14px;
    border-radius: 34px;
    overflow: hidden;
    isolation: isolate;
    background:
      radial-gradient(circle at 82% 16%, rgba(197,165,90,0.16), rgba(197,165,90,0) 32%),
      radial-gradient(circle at 16% 10%, rgba(73,169,255,0.16), rgba(73,169,255,0) 34%),
      linear-gradient(160deg, rgba(16,41,73,0.96) 0%, rgba(11,37,69,0.98) 100%);
    border: 1px solid rgba(255,255,255,0.09);
    box-shadow:
      0 22px 44px rgba(7,24,49,0.18),
      inset 0 1px 0 rgba(255,255,255,0.08);
  }

  .landing-hero-mobile-shell.is-text-only::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 18%, rgba(255,255,255,0) 34%),
      linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 42%);
    z-index: 1;
    pointer-events: none;
  }

  .landing-hero-mobile-shell.is-text-only::after {
    content: '';
    position: absolute;
    inset: auto 18px 14px;
    height: 54px;
    border-radius: 999px;
    background: radial-gradient(circle at 50% 50%, rgba(8,24,46,0.32), rgba(8,24,46,0) 72%);
    filter: blur(18px);
    opacity: 0.9;
    z-index: 0;
    pointer-events: none;
  }

  .landing-hero-mobile-card {
    position: relative;
    display: flex;
    align-items: flex-end;
    aspect-ratio: 21 / 25;
    min-height: 404px;
    overflow: hidden;
    padding: 10px;
    border-radius: 30px;
    isolation: isolate;
    background:
      radial-gradient(circle at 80% 14%, rgba(197,165,90,0.18), rgba(197,165,90,0) 30%),
      radial-gradient(circle at 16% 8%, rgba(73,169,255,0.14), rgba(73,169,255,0) 34%),
      linear-gradient(160deg, rgba(17,43,77,0.98) 0%, rgba(11,37,69,1) 100%);
    box-shadow:
      0 26px 56px rgba(7,24,49,0.24),
      0 10px 22px rgba(7,24,49,0.12);
  }

  .landing-hero-mobile-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 1px solid rgba(255,255,255,0.1);
    pointer-events: none;
    z-index: 3;
  }

  .landing-hero-mobile-card::after {
    content: '';
    position: absolute;
    inset: auto 22px 128px;
    height: 56px;
    border-radius: 999px;
    background: radial-gradient(circle at 50% 50%, rgba(11,37,69,0.34), rgba(11,37,69,0) 72%);
    filter: blur(18px);
    opacity: 0.88;
    z-index: 1;
    pointer-events: none;
  }

  .landing-hero-mobile-media,
  .landing-hero-mobile-fallback {
    position: absolute;
    inset: 10px;
    width: auto;
    height: auto;
    border-radius: 24px;
  }

  .landing-hero-mobile-media {
    object-fit: cover;
    object-position: center 14%;
    transform: scale(1.025);
    filter: saturate(0.9) contrast(1.02) brightness(0.92);
  }

  .landing-hero-mobile-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      radial-gradient(circle at 32% 24%, rgba(73,169,255,0.24), rgba(73,169,255,0) 32%),
      radial-gradient(circle at 76% 18%, rgba(197,165,90,0.24), rgba(197,165,90,0) 28%),
      linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02));
  }

  .landing-hero-mobile-overlay {
    position: absolute;
    inset: 10px;
    z-index: 1;
    border-radius: 24px;
    background:
      radial-gradient(circle at 58% 8%, rgba(255,255,255,0.14), rgba(255,255,255,0) 34%),
      linear-gradient(180deg, rgba(4,18,35,0.03) 0%, rgba(4,18,35,0.14) 22%, rgba(4,18,35,0.4) 48%, rgba(4,18,35,0.9) 100%),
      linear-gradient(135deg, rgba(11,37,69,0.04) 0%, rgba(11,37,69,0.26) 100%);
  }

  .landing-hero-mobile-copy {
    position: relative;
    z-index: 2;
    width: calc(100% - 28px);
    margin: 0 14px 14px;
    padding: 18px 17px 16px;
    border-radius: 24px;
    background:
      linear-gradient(180deg, rgba(10,24,46,0.12) 0%, rgba(10,24,46,0.5) 28%, rgba(10,24,46,0.84) 100%);
    border: 1px solid rgba(255,255,255,0.12);
    backdrop-filter: blur(14px);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.08),
      0 16px 30px rgba(5,20,39,0.2);
  }

  .landing-hero-mobile-copy.is-text-only {
    z-index: 2;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    padding: 18px 18px 16px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 26px;
    background:
      linear-gradient(180deg, rgba(10,24,46,0.06) 0%, rgba(10,24,46,0.34) 28%, rgba(10,24,46,0.72) 100%);
    backdrop-filter: blur(10px);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.05),
      0 10px 24px rgba(5,20,39,0.12);
  }

  .landing-hero-mobile-copy .landing-hero-mobile-kicker {
    display: block;
    margin-bottom: 8px;
    color: rgba(213,177,90,0.96);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  .landing-hero-mobile-title.ant-typography {
    margin: 0 0 12px !important;
    color: #FFFFFF;
    font-size: 27px !important;
    line-height: 1.12 !important;
    font-weight: 700;
    text-wrap: balance;
    max-width: 8.8ch;
    text-shadow: 0 8px 20px rgba(5,20,39,0.24);
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-title.ant-typography {
    margin-bottom: 14px !important;
    font-size: 34px !important;
    max-width: 11ch;
    text-shadow: none;
  }

  .landing-hero-mobile-subtitle,
  .landing-hero-mobile-subtitle p,
  .landing-hero-mobile-subtitle a {
    color: rgba(255,255,255,0.82) !important;
  }

  .landing-hero-mobile-subtitle {
    max-width: 100%;
    font-size: 13.5px;
    line-height: 1.56;
    text-wrap: pretty;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-subtitle {
    max-width: 34ch;
    font-size: 15px;
    line-height: 1.7;
  }

  .landing-hero-mobile-subtitle p {
    margin-bottom: 0;
  }

  .landing-hero-mobile-actions {
    display: flex;
    flex-direction: column;
    gap: 11px;
    margin-top: 15px;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions {
    position: relative;
    margin-top: 26px;
    padding: 14px 8px 0;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions::before {
    content: '';
    position: absolute;
    top: 0;
    left: 8px;
    right: 8px;
    height: 1px;
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.14) 18%, rgba(255,255,255,0.14) 82%, rgba(255,255,255,0) 100%);
    opacity: 0.9;
    pointer-events: none;
  }

  .landing-hero-mobile-actions .ant-btn {
    width: 100%;
    height: 50px;
    border-radius: 16px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.01em;
    box-shadow: none;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions .ant-btn {
    width: 100%;
    border-radius: 19px;
    margin: 0 auto;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions .ant-btn .anticon {
    font-size: 15px;
    transition: transform 180ms ease, opacity 180ms ease;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions .landing-btn-primary .anticon {
    transform: translateX(1px);
  }

  .landing-hero-mobile-actions .landing-btn-primary {
    border: none;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 38%),
      linear-gradient(135deg, #D7B45E 0%, #C79E43 100%);
    color: ${NAVY};
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.28),
      0 10px 20px rgba(197,165,90,0.18),
      0 1px 0 rgba(11,37,69,0.06);
  }

  .landing-hero-mobile-actions .landing-btn-secondary {
    border-color: rgba(255,255,255,0.18);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%);
    color: #FFFFFF;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.08),
      0 8px 18px rgba(6,22,41,0.12);
  }

  .landing-hero-mobile-fallback-card {
    text-align: center;
    color: #FFFFFF;
  }

  .landing-hero-mobile-fallback-card .anticon {
    font-size: 64px;
    color: ${GOLD};
  }

  .landing-hero-mobile-fallback-card .landing-hero-fallback-label {
    margin-top: 14px;
  }
}
`;

const LANDING_PAGE_STYLES = `
.landing-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 100% 0%, rgba(91,156,203,0.1) 0%, rgba(91,156,203,0) 26%),
    radial-gradient(circle at 0% 34%, rgba(197,165,90,0.08) 0%, rgba(197,165,90,0) 24%),
    linear-gradient(180deg, #F4F8FC 0%, #EEF4FA 56%, #F7FAFD 100%);
  color: ${NAVY};
  font-family: 'Avenir Next', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.landing-page .ant-typography,
.landing-page .ant-btn,
.landing-page a,
.landing-page span,
.landing-page div {
  font-family: inherit;
}

.landing-page .ant-typography h1,
.landing-page .ant-typography h2,
.landing-page .ant-typography h3,
.landing-page .ant-typography h4,
.landing-page .landing-hero-title {
  text-wrap: balance;
}

.skip-nav {
  position: absolute;
  top: -48px;
  left: 20px;
  z-index: 200;
  padding: 10px 14px;
  border-radius: 12px;
  background: #FFFFFF;
  color: ${NAVY};
  box-shadow: 0 12px 28px rgba(11, 37, 69, 0.16);
  text-decoration: none;
}

.skip-nav:focus {
  top: 12px;
}

.landing-section {
  position: relative;
}

.landing-section-inner {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.landing-section-title {
  position: relative;
  max-width: 760px;
  margin-bottom: 42px;
  text-align: left;
}

.landing-section-title-rule {
  width: 84px;
  height: 3px;
  margin-bottom: 18px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197, 165, 90, 0.22) 100%);
}

.landing-section-title.is-light .landing-section-title-rule {
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197, 165, 90, 0.08) 100%);
}

.landing-section-title .ant-typography {
  display: block;
  max-width: 720px;
}

.landing-section-panel {
  position: relative;
  overflow: visible;
  padding: 18px 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.landing-section-panel::before {
  display: none;
}

.landing-section-panel::after {
  display: none;
}

.landing-section-panel > * {
  position: relative;
  z-index: 1;
}

.landing-section-features {
  background:
    linear-gradient(180deg, #F3F7FB 0%, #FFFFFF 28%, #F6F9FC 100%),
    radial-gradient(circle at 6% 18%, rgba(197,165,90,0.05), rgba(197,165,90,0) 24%);
}

.landing-section-features .landing-section-inner {
  position: relative;
}

.landing-feature-panel {
  padding-top: 8px;
}

.landing-feature-panel::before {
  display: none;
}

.landing-section-workflow {
  overflow: hidden;
  background:
    linear-gradient(180deg, #FFFFFF 0%, #EEF5FA 100%);
}

.landing-section-workflow::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(11,37,69,0.98) 0%, rgba(19,49,92,0.96) 58%, rgba(27,65,117,0.94) 100%);
  clip-path: polygon(0 0, 100% 7%, 100% 88%, 0 100%);
  opacity: 1;
  pointer-events: none;
}

.landing-section-workflow .landing-section-inner {
  position: relative;
  z-index: 1;
}

.landing-section-workflow .landing-section-title .ant-typography,
.landing-section-workflow .landing-section-title span,
.landing-section-workflow .landing-section-title .ant-typography.ant-typography-h2 {
  color: #FFFFFF !important;
}

.landing-section-workflow .landing-section-title > span {
  color: rgba(255,255,255,0.7) !important;
}

.landing-workflow-panel {
  padding: 50px 0 54px;
  border: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

.landing-workflow-panel::before {
  background:
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: 58px 58px;
  opacity: 0.46;
}

.landing-workflow-panel::after {
  border-color: rgba(255,255,255,0.07);
}

.landing-section-resources {
  background:
    linear-gradient(180deg, #F2F7FB 0%, #FFF9EE 54%, #F7FAFD 100%);
}

.landing-resource-panel {
  padding-top: 28px;
}

.landing-resource-panel::before {
  background:
    linear-gradient(135deg, rgba(197,165,90,0.1), rgba(197,165,90,0) 36%),
    repeating-linear-gradient(90deg, rgba(197,165,90,0.04) 0 1px, transparent 1px 54px);
  opacity: 0.56;
}

.landing-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 72px;
  border-bottom: 1px solid rgba(19, 49, 92, 0.08);
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(16px);
  box-shadow: 0 10px 32px rgba(11, 37, 69, 0.04);
}

.landing-navbar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(197,165,90,0.42) 18%, rgba(197,165,90,0.18) 82%, transparent 100%);
}

.landing-nav-inner {
  display: flex;
  align-items: center;
  height: 100%;
}

.landing-logo-block {
  display: flex;
  align-items: center;
  margin-right: 48px;
}

.landing-nav-links {
  display: flex;
  flex: 1;
  gap: 34px;
}

.landing-nav-links a {
  position: relative;
  padding: 8px 0;
  color: #516178;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: color 160ms ease;
}

.landing-nav-links a::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197,165,90,0.18) 100%);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 160ms ease;
}

.landing-nav-links a:hover {
  color: ${NAVY};
}

.landing-nav-links a:hover::after {
  transform: scaleX(1);
}

.landing-nav-actions .ant-btn {
  height: 42px;
  padding: 0 18px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
}

.landing-nav-actions .landing-btn-secondary {
  border-color: rgba(11,37,69,0.28);
  color: ${NAVY};
  background: rgba(255,255,255,0.72);
}

.landing-nav-actions .landing-btn-primary {
  border-color: ${NAVY};
  background: ${NAVY};
  box-shadow: 0 14px 24px rgba(11,37,69,0.16);
}

.landing-hero-section {
  position: relative;
}

.landing-hero-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,0.015) 1px, transparent 1px);
  background-size: 72px 72px;
  opacity: 0.24;
  pointer-events: none;
}

.landing-hero-carousel {
  position: relative;
}

.landing-hero-arrow {
  position: absolute;
  top: 50%;
  z-index: 10;
  width: 58px;
  height: 58px;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 50%;
  background: rgba(6, 22, 41, 0.34);
  box-shadow: 0 18px 38px rgba(7,24,49,0.12);
  color: ${GOLD};
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(16px);
}

.landing-hero-arrow.is-prev {
  left: 20px;
  transform: translateY(-50%);
}

.landing-hero-arrow.is-next {
  right: 20px;
  transform: translateY(-50%);
}

.landing-page .hero-dots {
  bottom: 18px;
}

.landing-page .hero-dots li {
  width: 20px;
}

.landing-page .hero-dots li button {
  height: 3px;
  border-radius: 999px;
  background: rgba(255,255,255,0.18) !important;
}

.landing-page .hero-dots li.slick-active button {
  background: rgba(255,255,255,0.92) !important;
}

.landing-hero-copy-shell::after {
  display: none;
}

.landing-hero-copy-inner .landing-hero-subtitle p {
  margin-bottom: 0;
}

.landing-hero-copy-inner .landing-btn-primary,
.landing-hero-copy-inner .landing-btn-secondary {
  height: 50px;
  padding: 0 30px;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
}

.landing-hero-copy-inner .landing-btn-primary {
  background: linear-gradient(135deg, #D5B15A 0%, #B8923E 100%);
  border: none;
  color: ${NAVY};
  box-shadow: 0 16px 32px rgba(197,165,90,0.24);
}

.landing-hero-copy-inner .landing-btn-secondary {
  border-color: rgba(255,255,255,0.24);
  background: rgba(255,255,255,0.03);
  color: #FFFFFF;
}

.landing-trust-section {
  position: relative;
  z-index: 2;
  margin-top: -36px;
  padding: 0 0 36px;
  background: linear-gradient(180deg, rgba(244,247,251,0) 0%, #F3F7FB 48px);
}

.landing-trust-card {
  position: relative;
  height: 100%;
  overflow: hidden;
  padding: 18px 18px 17px;
  border: 1px solid rgba(19,49,92,0.07);
  border-radius: 16px;
  background: rgba(255,255,255,0.96);
  box-shadow: 0 12px 28px rgba(11,37,69,0.065);
}

.landing-trust-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 3px;
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197,165,90,0.26) 100%);
}

.landing-trust-icon {
  width: 34px;
  height: 34px;
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid rgba(197,165,90,0.26);
  background: linear-gradient(180deg, rgba(197,165,90,0.12) 0%, rgba(197,165,90,0.035) 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-trust-label {
  color: ${NAVY};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}

.landing-trust-desc {
  margin-top: 10px;
  color: #617086;
  font-size: 12.5px;
  line-height: 1.62;
}

.features-masonry,
.resources-masonry {
  display: flex;
  width: auto;
  margin-left: -18px;
}

.features-masonry-column,
.resources-masonry-column {
  padding-left: 18px;
  background-clip: padding-box;
}

.features-masonry-column > div,
.resources-masonry-column > a,
.resources-masonry-column > div {
  margin-bottom: 18px;
}

.landing-surface-card.ant-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(19,49,92,0.07);
  border-radius: 18px;
  background: rgba(255,255,255,0.92);
  box-shadow: 0 10px 28px rgba(11,37,69,0.055);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.landing-surface-card.ant-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197,165,90,0.18) 100%);
}

.landing-surface-card.ant-card:hover {
  transform: translateY(-2px);
  border-color: rgba(197,165,90,0.28);
  box-shadow: 0 18px 36px rgba(11,37,69,0.085);
}

.landing-feature-card.ant-card,
.landing-resource-card.ant-card {
  height: 100%;
}

.landing-feature-card.ant-card {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,253,0.96) 100%);
}

.landing-feature-item.is-alt .landing-feature-card.ant-card {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,249,252,0.96) 100%);
}

.landing-resource-card.ant-card {
  border-color: rgba(197,165,90,0.16);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,252,246,0.97) 100%);
}

.landing-feature-card.ant-card::after,
.landing-resource-card.ant-card::after {
  display: none;
}

.landing-feature-card .ant-card-body,
.landing-resource-card .ant-card-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.landing-feature-header,
.landing-resource-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
}

.landing-feature-index {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-width: auto;
  height: auto;
  padding: 0;
  border: none;
  background: none;
  color: ${GOLD};
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
  box-shadow: none;
}

.landing-feature-icon,
.landing-resource-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  border: 1px solid rgba(197,165,90,0.2);
  background: linear-gradient(180deg, rgba(197,165,90,0.14) 0%, rgba(197,165,90,0.04) 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.56);
}

.landing-feature-card .ant-typography.ant-typography-h4,
.landing-resource-card .ant-typography.ant-typography-h4 {
  font-size: 20px;
  line-height: 1.34;
  letter-spacing: -0.02em;
}

.landing-feature-copy,
.landing-resource-copy {
  color: #566881;
  font-size: 14px;
  line-height: 1.82;
  text-wrap: pretty;
}

.landing-resource-copy {
  flex: 1;
}

.landing-feature-copy p,
.landing-resource-copy p {
  margin-bottom: 0;
}

.landing-workflow-panel {
  padding-bottom: 40px;
}

.landing-workflow-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;
}

.landing-workflow-grid::before {
  content: '';
  position: absolute;
  left: 8%;
  right: 8%;
  top: 22px;
  height: 1px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(197,165,90,0.42) 50%, rgba(255,255,255,0.08) 100%);
  z-index: 0;
}

.landing-step-shell {
  position: relative;
  min-width: 0;
  z-index: 1;
}

.landing-step-marker {
  position: relative;
  z-index: 1;
  width: 44px;
  height: 44px;
  margin: 0 0 16px 6px;
  border-radius: 999px;
  border: 1px solid rgba(197,165,90,0.44);
  background: ${GOLD};
  color: ${NAVY};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  box-shadow:
    0 10px 24px rgba(5,18,36,0.18),
    inset 0 1px 0 rgba(255,255,255,0.16);
}

.landing-step-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 23px 22px 22px 24px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(244,248,252,0.92) 100%);
  box-shadow:
    0 14px 30px rgba(5,18,36,0.14),
    inset 0 1px 0 rgba(255,255,255,0.72);
  backdrop-filter: none;
}

.landing-step-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 22px;
  bottom: 22px;
  width: 3px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(197,165,90,0.92), rgba(197,165,90,0.24));
}

.landing-step-top {
  display: block;
  margin-bottom: 12px;
}

.landing-step-heading {
  min-width: 0;
  padding-top: 0;
}

.landing-step-label {
  display: none;
}

.landing-step-number {
  all: unset;
}

.landing-step-title.ant-typography {
  margin: 0 !important;
  color: ${NAVY} !important;
  font-size: 19px !important;
  line-height: 1.4 !important;
  letter-spacing: -0.02em;
}

.landing-step-copy {
  margin-top: 0;
  color: #5D6D84;
  font-size: 14px;
  line-height: 1.78;
  text-wrap: pretty;
}

.landing-step-copy p {
  margin-bottom: 0;
}

.landing-resource-link {
  text-decoration: none;
  display: block;
  color: inherit;
}

.landing-resource-arrow {
  margin-top: 18px;
  color: ${NAVY};
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.landing-testimonials-section {
  position: relative;
  overflow: hidden;
  padding: 84px 0;
  background: linear-gradient(135deg, #0B2545 0%, #112F57 58%, #173E72 100%);
}

.landing-testimonials-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 12% 18%, rgba(197,165,90,0.14), transparent 28%),
    radial-gradient(circle at 86% 74%, rgba(73,169,255,0.14), transparent 28%),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,0.015) 1px, transparent 1px);
  background-size: auto, auto, 64px 64px, 64px 64px;
  opacity: 0.32;
  pointer-events: none;
}

.landing-testimonial-card {
  position: relative;
  height: 100%;
  overflow: hidden;
  padding: 30px 28px 24px;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 24px;
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(18px);
}

.landing-testimonial-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 2px;
  background: linear-gradient(90deg, ${GOLD} 0%, rgba(197,165,90,0.08) 100%);
}

.landing-cta-section {
  padding: 34px 0 80px;
}

.landing-cta-panel {
  position: relative;
  overflow: hidden;
  padding: 34px 38px;
  border-radius: 26px;
  background: linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 58%, ${NAVY_LIGHT} 100%);
  box-shadow: 0 28px 56px rgba(11,37,69,0.14);
}

.landing-cta-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 88% 26%, rgba(197,165,90,0.16), transparent 22%),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: auto, 58px 58px, 58px 58px;
  opacity: 0.22;
  pointer-events: none;
}

.landing-cta-panel > * {
  position: relative;
  z-index: 1;
}

.landing-cta-actions .ant-btn {
  height: 48px;
  padding: 0 28px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
}

.landing-cta-actions .landing-btn-primary {
  border: none;
  background: linear-gradient(135deg, #D5B15A 0%, #B8923E 100%);
  color: ${NAVY};
}

.landing-cta-actions .landing-btn-secondary {
  border-color: rgba(255,255,255,0.22);
  color: #FFFFFF;
  background: rgba(255,255,255,0.04);
}

@media (max-width: 991px) {
  .landing-section-inner {
    width: min(100% - 32px, 1200px);
  }

  .landing-section-title {
    margin-bottom: 34px;
  }

  .landing-section-panel {
    padding: 20px 0;
    border-radius: 0;
  }

  .landing-section-panel::after {
    display: none;
  }

  .landing-nav-links {
    display: none;
  }

  .landing-logo-block {
    margin-right: auto;
  }

  .landing-nav-actions {
    gap: 8px !important;
  }

  .landing-nav-actions .ant-btn {
    padding: 0 14px;
  }

  .landing-trust-section {
    margin-top: -8px;
    padding-top: 10px;
    padding-bottom: 28px;
    background: linear-gradient(180deg, rgba(244,247,251,0) 0%, rgba(244,247,251,0.72) 18px, #F4F7FB 44px);
  }

  .landing-workflow-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }

  .landing-workflow-grid::before {
    display: none;
  }

  .landing-step-card {
    height: auto;
    min-height: 100%;
  }

  .landing-testimonials-section {
    padding: 70px 0;
  }

  .landing-cta-section {
    padding: 20px 0 64px;
  }

  .landing-cta-panel {
    padding: 28px 24px;
  }
}

@media (max-width: 768px) {
  .landing-section-workflow {
    padding-top: 24px !important;
    padding-bottom: 34px !important;
  }

  .landing-workflow-panel {
    padding-bottom: 8px;
  }

  .landing-workflow-grid {
    display: flex;
    flex-direction: column;
    gap: 14px;
    grid-template-columns: none;
  }

  .landing-step-shell {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .landing-step-marker {
    width: 42px;
    height: 42px;
    margin: 2px 0 0;
    border-radius: 14px;
    font-size: 15px;
  }

  .landing-step-card {
    min-width: 0;
    height: auto;
    min-height: 0;
    padding: 18px 17px 17px;
    border-radius: 18px;
  }

  .landing-step-top {
    margin-bottom: 8px;
  }

  .landing-step-copy {
    overflow-wrap: anywhere;
  }

  .landing-step-copy p {
    margin: 0;
  }
}

@media (max-width: 576px) {
  .landing-section-inner {
    width: min(100% - 24px, 1200px);
  }

  .landing-section-workflow::before {
    inset: 10px 0 30px;
  }

  .landing-section-workflow {
    padding-top: 24px !important;
    padding-bottom: 34px !important;
  }

  .landing-workflow-panel {
    padding-bottom: 8px;
  }

  .landing-hero-mobile-card {
    min-height: 382px;
    padding: 8px;
    border-radius: 26px;
  }

  .landing-hero-mobile-media,
  .landing-hero-mobile-fallback,
  .landing-hero-mobile-overlay {
    inset: 8px;
    border-radius: 20px;
  }

  .landing-hero-mobile-copy {
    width: calc(100% - 18px);
    margin: 0 9px 9px;
    padding: 16px 15px 14px;
    border-radius: 20px;
  }

  .landing-hero-mobile-copy.is-text-only {
    width: min(100%, 100%);
    margin: 0;
    padding: 16px 15px 14px;
    border-radius: 24px;
  }

  .landing-hero-mobile-title.ant-typography {
    font-size: 25px !important;
    max-width: 8.2ch;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-title.ant-typography {
    font-size: 30px !important;
    max-width: 10.5ch;
  }

  .landing-hero-mobile-subtitle {
    font-size: 13px;
    line-height: 1.52;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-subtitle {
    font-size: 14px;
    line-height: 1.64;
  }

  .landing-hero-mobile-shell.is-text-only {
    padding: 10px;
    border-radius: 28px;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions {
    padding: 12px 6px 0;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions::before {
    left: 6px;
    right: 6px;
  }

  .landing-hero-mobile-actions .ant-btn {
    height: 46px;
    font-size: 14px;
  }

  .landing-section-title .ant-typography.ant-typography-h2 {
    font-size: 28px !important;
  }

  .landing-section-panel {
    padding: 18px 0;
  }

  .landing-section-panel::after {
    display: none;
  }

  .landing-hero-arrow {
    display: none;
  }

  .landing-trust-card,
  .landing-step-card,
  .landing-testimonial-card,
  .landing-surface-card.ant-card {
    border-radius: 20px;
  }

  .landing-nav-actions .ant-btn {
    min-width: 0;
    height: 44px;
    padding: 0 11px;
    font-size: 13px;
  }

  .landing-brand-logo {
    width: auto;
    max-width: 126px !important;
    height: 42px !important;
  }

  .landing-nav-inner {
    min-height: 68px;
    padding: 0 14px !important;
  }

  .landing-nav-actions {
    gap: 6px !important;
  }

  .landing-trust-grid.ant-row {
    margin-right: -6px !important;
    margin-left: -6px !important;
    row-gap: 12px !important;
  }

  .landing-trust-grid > .ant-col {
    padding-right: 6px !important;
    padding-left: 6px !important;
  }

  .landing-trust-card {
    padding: 14px 13px 15px;
    border-radius: 14px;
  }

  .landing-trust-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 10px;
  }

  .landing-trust-label {
    font-size: 14px;
    line-height: 1.42;
  }

  .landing-trust-desc {
    margin-top: 7px;
    font-size: 12px;
    line-height: 1.55;
  }

  .landing-feature-card .ant-card-body,
  .landing-resource-card .ant-card-body {
    padding: 22px !important;
  }

  .landing-feature-header,
  .landing-resource-header {
    margin-bottom: 18px;
  }

  .landing-feature-index {
    min-width: auto;
    height: auto;
    padding: 0;
    font-size: 16px;
  }

  .landing-feature-icon,
  .landing-resource-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .landing-feature-card .ant-typography.ant-typography-h4,
  .landing-resource-card .ant-typography.ant-typography-h4,
  .landing-step-title.ant-typography {
    font-size: 18px !important;
  }

  .landing-workflow-grid {
    display: flex;
    flex-direction: column;
    gap: 14px;
    grid-template-columns: none;
  }

  .landing-step-shell {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .landing-step-marker {
    width: 42px;
    height: 42px;
    margin: 2px 0 0;
    border-radius: 14px;
    font-size: 15px;
  }

  .landing-step-card {
    min-width: 0;
    height: auto;
    min-height: 0;
    padding: 18px 17px 17px;
    border-radius: 18px;
  }

  .landing-step-top {
    margin-bottom: 8px;
  }

  .landing-step-copy,
  .landing-feature-copy,
  .landing-resource-copy {
    font-size: 13.5px;
    line-height: 1.72;
    overflow-wrap: anywhere;
  }

  .landing-step-copy p {
    margin: 0;
  }
}

/* Landing page refinement: a quieter, clinical interface with product-workflow emphasis. */
body {
  margin: 0;
}

.landing-page {
  background: #F6F8FB;
}

.landing-page :focus-visible {
  outline: 3px solid rgba(197,165,90,0.72);
  outline-offset: 3px;
}

.landing-navbar {
  height: 70px;
  background: rgba(255,255,255,0.96);
  box-shadow: 0 4px 18px rgba(11,37,69,0.055);
}

.landing-navbar::after,
.landing-section-title-rule,
.landing-surface-card.ant-card::before,
.landing-testimonial-card::before {
  background: ${GOLD};
}

.landing-nav-links {
  gap: 30px;
}

.landing-nav-links a {
  color: #5A687B;
  font-weight: 600;
}

.landing-nav-actions .ant-btn,
.landing-hero-copy-inner .landing-btn-primary,
.landing-hero-copy-inner .landing-btn-secondary,
.landing-cta-actions .ant-btn {
  border-radius: 10px;
}

.landing-nav-actions .landing-btn-primary {
  box-shadow: none;
}

.landing-hero-section::before,
.landing-testimonials-section::before,
.landing-cta-panel::before {
  display: none;
}

.landing-hero-arrow {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(4,20,38,0.52);
  box-shadow: none;
}

.landing-hero-section {
  background: linear-gradient(118deg, #082441 0%, #10345F 100%) !important;
}

.landing-hero-stage {
  min-height: 480px;
  grid-template-columns: minmax(0, 1fr) minmax(500px, 1fr);
  gap: 48px;
}

.landing-hero-copy-shell::before {
  display: none;
}

.landing-hero-copy-inner {
  padding: 0;
}

.landing-hero-copy-inner .landing-hero-title {
  max-width: min(16.5ch, 100%);
  margin: 14px 0 18px !important;
  font-size: clamp(40px, 3.6vw, 52px) !important;
  line-height: 1.12 !important;
  letter-spacing: -0.045em;
}

.landing-hero-copy-inner .landing-hero-subtitle {
  max-width: 34ch !important;
  color: rgba(255,255,255,0.76) !important;
  font-size: 17px !important;
  line-height: 1.75 !important;
}

.landing-hero-copy-inner .landing-btn-primary,
.landing-cta-actions .landing-btn-primary {
  background: ${GOLD};
  box-shadow: none;
}

.landing-hero-copy-inner .landing-btn-primary:hover,
.landing-cta-actions .landing-btn-primary:hover {
  background: #D2B56E;
}

.landing-hero-copy-inner .landing-btn-secondary {
  background: transparent;
  border-color: rgba(255,255,255,0.34);
}

.landing-hero-visual-shell {
  min-height: 390px;
}

.landing-hero-visual-frame {
  height: 354px;
  min-height: 354px;
  border: 1px solid rgba(255,255,255,0.22);
  border-radius: 18px;
  background: rgba(255,255,255,0.06);
  box-shadow: 0 20px 42px rgba(3,17,34,0.22);
  backdrop-filter: none;
}

.landing-hero-visual-frame::before {
  inset: 12px 12px auto;
  height: 24px;
  border-radius: 8px 8px 4px 4px;
  background: rgba(255,255,255,0.11);
}

.landing-hero-visual-frame::after,
.landing-hero-visual-media-wrap::before {
  background: linear-gradient(180deg, rgba(11,37,69,0.04) 0%, rgba(11,37,69,0.2) 100%);
}

.landing-hero-visual-media-wrap {
  width: calc(100% - 24px);
  height: calc(100% - 24px);
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.48);
}

.landing-hero-fallback-visual {
  background: linear-gradient(145deg, #123D67 0%, #0B2545 100%);
}

.landing-trust-section {
  margin-top: 0;
  padding: 28px 0;
  background: #FFFFFF;
  border-bottom: 1px solid #E4EAF1;
}

.landing-trust-grid.ant-row {
  row-gap: 0 !important;
}

.landing-trust-card {
  min-height: 88px;
  padding: 8px 22px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.landing-trust-card::before {
  top: 4px;
  bottom: 4px;
  left: 0;
  right: auto;
  width: 1px;
  height: auto;
  background: #E1E8F0;
}

.landing-trust-grid > .ant-col:first-child .landing-trust-card::before {
  display: none;
}

.landing-trust-icon {
  width: auto;
  height: auto;
  margin: 0 0 10px;
  border: 0;
  border-radius: 0;
  background: none;
}

.landing-trust-label {
  font-size: 15px;
}

.landing-trust-desc {
  margin-top: 6px;
  color: #6B7788;
}

.landing-section-features,
.landing-section-resources {
  background: #F6F8FB;
}

.landing-section-title {
  margin-bottom: 34px;
}

.landing-section-title-rule {
  width: 42px;
  height: 2px;
  margin-bottom: 14px;
}

.landing-feature-layout {
  display: grid;
  grid-template-columns: minmax(280px, 0.82fr) minmax(0, 1.18fr);
  gap: 28px;
  align-items: stretch;
}

.landing-feature-index-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #DDE5EE;
}

.landing-feature-index-row {
  display: grid;
  grid-template-columns: 42px 32px minmax(0, 1fr) 18px;
  gap: 12px;
  align-items: center;
  min-height: 70px;
  border-bottom: 1px solid #DDE5EE;
  color: #304962;
}

.landing-feature-index-row:hover {
  color: ${NAVY};
}

.landing-feature-index,
.landing-feature-index-row .landing-feature-index {
  color: ${GOLD};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.landing-feature-index-icon,
.landing-feature-focus-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-feature-index-title {
  min-width: 0;
  font-size: 15px;
  font-weight: 650;
  line-height: 1.4;
}

.landing-feature-index-arrow {
  color: #92A1B2;
  font-size: 13px;
}

.landing-feature-focus {
  padding: 32px;
  border: 1px solid #D9E3ED;
  border-radius: 14px;
  background: #FFFFFF;
  box-shadow: 0 10px 28px rgba(11,37,69,0.045);
}

.landing-feature-focus-kicker {
  margin-bottom: 14px;
  color: ${GOLD};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.landing-feature-focus-title.ant-typography {
  max-width: 13ch;
  margin-bottom: 14px;
  color: ${NAVY};
  font-size: 28px;
  line-height: 1.25;
  letter-spacing: -0.03em;
}

.landing-feature-focus-copy.ant-typography {
  max-width: 45ch;
  margin-bottom: 28px;
  color: #5D6C80;
  font-size: 15px;
  line-height: 1.78;
}

.landing-feature-focus-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid #E3E9F0;
  border-left: 1px solid #E3E9F0;
}

.landing-feature-focus-item {
  min-height: 124px;
  padding: 18px 20px;
  border-right: 1px solid #E3E9F0;
  border-bottom: 1px solid #E3E9F0;
}

.landing-feature-focus-item-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: ${NAVY};
  font-size: 14px;
  font-weight: 700;
}

.landing-feature-focus-item .landing-feature-copy {
  font-size: 13px;
  line-height: 1.62;
}

.landing-section-workflow {
  background: ${NAVY} !important;
}

.landing-section-workflow::before {
  display: none;
}

.landing-workflow-panel {
  padding: 58px 0 64px;
}

.landing-workflow-grid {
  gap: 0;
  border-top: 1px solid rgba(255,255,255,0.2);
}

.landing-workflow-grid::before {
  display: none;
}

.landing-step-shell {
  padding: 22px 22px 0 0;
  border-right: 1px solid rgba(255,255,255,0.16);
}

.landing-step-shell + .landing-step-shell {
  padding-left: 22px;
}

.landing-step-shell:last-child {
  border-right: 0;
}

.landing-step-marker {
  width: auto;
  height: auto;
  margin: 0 0 28px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: ${GOLD};
  font-size: 13px;
  letter-spacing: 0.1em;
}

.landing-step-card {
  min-height: 154px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.landing-step-card::before {
  display: none;
}

.landing-step-title.ant-typography {
  color: #FFFFFF !important;
  font-size: 19px !important;
}

.landing-step-copy {
  color: rgba(255,255,255,0.68);
  font-size: 14px;
  line-height: 1.74;
}

.landing-surface-card.ant-card,
.landing-resource-card.ant-card {
  border-color: #DDE5EE;
  border-radius: 12px;
  background: #FFFFFF;
  box-shadow: none;
}

.landing-surface-card.ant-card:hover {
  transform: translateY(-3px);
  border-color: rgba(197,165,90,0.7);
  box-shadow: 0 12px 28px rgba(11,37,69,0.08);
}

.landing-resource-icon {
  width: auto;
  height: auto;
  border: 0;
  border-radius: 0;
  background: none;
  box-shadow: none;
}

.landing-testimonials-section {
  padding: 76px 0;
  background: #102F54;
}

.landing-testimonial-card {
  min-height: 238px;
  padding: 28px;
  border-radius: 12px;
  background: rgba(255,255,255,0.045);
  backdrop-filter: none;
}

.landing-cta-section {
  padding: 56px 0 72px;
  background: #F6F8FB;
}

.landing-cta-panel {
  padding: 36px 40px;
  border: 1px solid rgba(11,37,69,0.12);
  border-radius: 14px;
  background: ${NAVY};
  box-shadow: none;
}

@media (max-width: 991px) {
  .landing-hero-stage {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .landing-hero-visual-shell {
    min-height: 300px;
  }

  .landing-feature-layout {
    grid-template-columns: 1fr;
  }

  .landing-feature-focus-title.ant-typography {
    max-width: none;
  }

  .landing-step-shell {
    padding: 22px 20px 0 0;
  }

  .landing-step-shell + .landing-step-shell {
    padding-left: 20px;
  }
}

@media (max-width: 768px) {
  .landing-hero-section {
    padding: 40px 0 54px !important;
  }

  .landing-hero-mobile-shell.is-text-only {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .landing-hero-mobile-shell.is-text-only::before,
  .landing-hero-mobile-shell.is-text-only::after {
    display: none;
  }

  .landing-hero-mobile-copy.is-text-only {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-title.ant-typography {
    max-width: 11.5ch;
    font-size: 34px !important;
    letter-spacing: -0.045em;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions {
    align-items: stretch;
    padding: 14px 0 0;
  }

  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions::before {
    left: 0;
    right: 0;
  }

  .landing-hero-mobile-actions .ant-btn,
  .landing-hero-mobile-copy.is-text-only .landing-hero-mobile-actions .ant-btn {
    border-radius: 10px;
  }

  .landing-trust-section {
    padding: 20px 0 8px;
  }

  .landing-trust-card {
    min-height: 98px;
    padding: 10px 12px 18px;
  }

  .landing-trust-grid > .ant-col:nth-child(odd) .landing-trust-card::before {
    display: none;
  }

  .landing-feature-focus {
    padding: 24px 20px;
  }

  .landing-feature-focus-grid {
    grid-template-columns: 1fr;
  }

  .landing-feature-focus-item {
    min-height: 0;
  }

  .landing-workflow-panel {
    padding: 48px 0;
  }

  .landing-workflow-grid {
    border-top: 0;
  }

  .landing-step-shell,
  .landing-step-shell + .landing-step-shell {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 14px;
    padding: 0 0 22px;
    border-right: 0;
    border-bottom: 1px solid rgba(255,255,255,0.16);
  }

  .landing-step-shell:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  .landing-step-marker {
    margin: 2px 0 0;
  }

  .landing-step-card {
    min-height: 0;
  }

  .landing-cta-panel {
    padding: 28px 24px;
  }
}

/* Interactive capability navigator. */
.landing-feature-layout {
  grid-template-columns: minmax(300px, 0.78fr) minmax(0, 1.22fr);
  gap: 24px;
}

.landing-feature-index-list {
  padding: 7px;
  border: 1px solid #DCE4ED;
  border-radius: 14px;
  background: #FFFFFF;
  box-shadow: 0 10px 28px rgba(11,37,69,0.04);
}

.landing-feature-index-row {
  width: 100%;
  min-height: 68px;
  padding: 0 14px;
  border: 0;
  border-bottom: 1px solid #E5EAF0;
  border-radius: 0;
  background: transparent;
  color: #334B64;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 180ms ease, color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.landing-feature-index-row:last-child {
  border-bottom-color: transparent;
}

.landing-feature-index-row:hover {
  background: #F2F6FA;
  color: ${NAVY};
}

.landing-feature-index-row.is-active {
  border-bottom-color: transparent;
  border-radius: 9px;
  background: ${NAVY};
  color: #FFFFFF;
  box-shadow: 0 10px 22px rgba(11,37,69,0.16);
  transform: translateX(3px);
}

.landing-feature-index-row.is-active + .landing-feature-index-row {
  border-top-color: transparent;
}

.landing-feature-index-row.is-active .landing-feature-index-title {
  color: #FFFFFF;
}

.landing-feature-index-row.is-active .landing-feature-index-action,
.landing-feature-index-row.is-active .landing-feature-index-arrow {
  color: ${GOLD};
}

.landing-feature-index-footer {
  margin-top: auto;
  padding: 18px 14px 16px;
  border-top: 1px solid #E2E8EF;
  background: #F6F8FB;
}

.landing-feature-index-footer span {
  color: ${GOLD};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.landing-feature-index-footer p {
  margin: 8px 0 0;
  color: #66768A;
  font-size: 12px;
  line-height: 1.6;
}

.landing-feature-index-action {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  color: #8291A3;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.landing-feature-index-arrow {
  transition: transform 180ms ease;
}

.landing-feature-index-row:hover .landing-feature-index-arrow,
.landing-feature-index-row.is-active .landing-feature-index-arrow {
  transform: translateX(3px);
}

.landing-feature-focus {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 38px;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  background: #0D2D50;
  box-shadow: 0 18px 38px rgba(11,37,69,0.13);
  color: #FFFFFF;
}

.landing-feature-focus-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.landing-feature-focus-kicker {
  margin-bottom: 12px;
  color: ${GOLD};
  font-size: 12px;
  letter-spacing: 0.08em;
}

.landing-feature-focus-title.ant-typography {
  max-width: 14ch;
  margin: 0 !important;
  color: #FFFFFF;
  font-size: 32px;
  line-height: 1.22;
}

.landing-feature-focus-phase {
  flex: 0 0 auto;
  padding: 7px 10px;
  border: 1px solid rgba(197,165,90,0.48);
  border-radius: 6px;
  color: #E2C77F;
  font-size: 12px;
  font-weight: 700;
}

.landing-feature-focus-copy {
  max-width: min(100%, 38rem);
  margin: 24px 0 20px;
  color: rgba(255,255,255,0.74);
  font-size: 16px;
  line-height: 1.72;
  text-wrap: pretty;
}

.landing-feature-focus-copy p {
  margin: 0;
}

.landing-feature-value {
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 18px;
  align-items: baseline;
  max-width: 590px;
  padding: 16px 0;
  border-top: 1px solid rgba(255,255,255,0.14);
  border-bottom: 1px solid rgba(255,255,255,0.14);
}

.landing-feature-value span,
.landing-feature-detail-label {
  color: rgba(255,255,255,0.48);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.05em;
}

.landing-feature-value strong {
  color: rgba(255,255,255,0.86);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.65;
}

.landing-feature-detail-label {
  margin: 22px 0 9px;
}

.landing-feature-detail-label.is-evidence {
  margin-top: 20px;
}

.landing-feature-flow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 22px minmax(0, 1fr) 22px minmax(0, 1fr);
  align-items: center;
  border-top: 1px solid rgba(255,255,255,0.14);
  border-bottom: 1px solid rgba(255,255,255,0.14);
}

.landing-feature-flow span {
  padding: 17px 10px;
  color: rgba(255,255,255,0.62);
  font-size: 13px;
  font-weight: 650;
  text-align: center;
}

.landing-feature-flow span.is-current {
  background: rgba(197,165,90,0.12);
  color: #F0D99B;
}

.landing-feature-flow .anticon {
  color: rgba(255,255,255,0.3) !important;
  font-size: 12px;
  text-align: center;
}

.landing-feature-evidence-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 22px;
  border: 1px solid rgba(255,255,255,0.14);
}

.landing-feature-evidence-item {
  min-width: 0;
  padding: 18px 20px;
}

.landing-feature-evidence-item + .landing-feature-evidence-item {
  border-left: 1px solid rgba(255,255,255,0.14);
}

.landing-feature-evidence-item span {
  display: block;
  margin-bottom: 7px;
  color: rgba(255,255,255,0.5);
  font-size: 12px;
}

.landing-feature-evidence-item strong {
  display: block;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.55;
}

@media (max-width: 991px) {
  .landing-feature-layout {
    grid-template-columns: 1fr;
  }

  .landing-feature-focus {
    min-height: 420px;
  }
}

@media (max-width: 768px) {
  .landing-feature-index-list {
    display: flex;
    flex-direction: row;
    gap: 7px;
    padding: 7px;
    overflow-x: auto;
    border-top: 1px solid #DCE4ED;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
  }

  .landing-feature-index-list::-webkit-scrollbar {
    display: none;
  }

  .landing-feature-index-row {
    flex: 0 0 218px;
    grid-template-columns: 30px 28px minmax(0, 1fr);
    min-height: 62px;
    padding: 0 12px;
    border: 0;
    border-radius: 9px;
    background: #F4F7FA;
    scroll-snap-align: start;
  }

  .landing-feature-index-row.is-active {
    transform: none;
  }

  .landing-feature-index-action {
    display: none;
  }

  .landing-feature-index-footer {
    display: none;
  }

  .landing-feature-focus {
    min-height: 0;
    padding: 26px 22px;
  }

  .landing-feature-focus-head {
    display: block;
  }

  .landing-feature-focus-title.ant-typography {
    max-width: none;
    font-size: 27px;
  }

  .landing-feature-focus-phase {
    display: inline-flex;
    margin-top: 16px;
  }

  .landing-feature-focus-copy {
    margin: 20px 0 24px;
    font-size: 14px;
  }

  .landing-feature-value {
    grid-template-columns: 1fr;
    gap: 6px;
    max-width: none;
  }

  .landing-feature-flow {
    grid-template-columns: 1fr 16px 1fr 16px 1fr;
  }

  .landing-feature-flow span {
    padding: 14px 5px;
    font-size: 11px;
  }

  .landing-feature-evidence-grid {
    grid-template-columns: 1fr;
  }

  .landing-feature-evidence-item + .landing-feature-evidence-item {
    border-top: 1px solid rgba(255,255,255,0.14);
    border-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-page *,
  .landing-page *::before,
  .landing-page *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
`;

const HeroCarousel: React.FC<{
  slides: any[];
  fallbackHero?: any;
  onRegister: () => void;
  loading?: boolean;
}> = ({ slides, fallbackHero, onRegister, loading = false }) => {
  const carouselRef = useRef<any>(null);
  const { isPhone } = useResponsive();
  const activeSlides = (slides || [])
    .filter(s => s.is_active !== false)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const resolveImageUrl = (url: string) => {
    return resolvePublicAssetUrl(url);
  };

  const renderHeroTitle = (title: string) => {
    return <HeroTitleContent title={title} />;
  };

  const renderPrimaryAction = (hero: any, block = false) => (
    hero.link_url ? (
      <Button
        className="landing-btn-primary"
        type="primary"
        size="large"
        icon={<ArrowRightOutlined />}
        href={hero.link_url}
        block={block}
        style={{ color: NAVY }}
      >
        {hero.link_text || hero.cta_text || 'Start'}
      </Button>
    ) : (
      <Button
        className="landing-btn-primary"
        type="primary"
        size="large"
        icon={<ArrowRightOutlined />}
        onClick={onRegister}
        block={block}
        style={{ color: NAVY }}
      >
        {hero.cta_text || 'Start'}
      </Button>
    )
  );

  const renderSecondaryAction = (block = false) => (
    <Button className="landing-btn-secondary" size="large" ghost href="#how-it-works" block={block}>
      查看工作流程
    </Button>
  );

  const renderHeroScene = (hero: any, imageUrl?: string) => {
    if (isPhone) {
      return (
        <section className="landing-hero-section" style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 50%, ${NAVY_LIGHT} 100%)`,
          padding: '64px 0 82px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', position: 'relative', zIndex: 1 }}>
            <div className="landing-hero-mobile-shell is-text-only">
              <div className="landing-hero-mobile-copy is-text-only">
                <Text className="landing-hero-mobile-kicker">
                  MODEL-INFORMED PRECISION DOSING
                </Text>
                <Title className="landing-hero-mobile-title">
                  {renderHeroTitle(hero.title)}
                </Title>
                <div
                  className="landing-hero-mobile-subtitle"
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(hero.subtitle || '') }}
                />
                <div className="landing-hero-mobile-actions">
                  {renderPrimaryAction(hero, true)}
                  {renderSecondaryAction(true)}
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section className="landing-hero-section" style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 50%, ${NAVY_LIGHT} 100%)`,
        padding: '52px 0 74px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <div className="landing-hero-stage">
            <div className="landing-hero-copy-shell">
              <div className="landing-hero-copy-inner">
                <Text style={{ color: GOLD, fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase' }}>
                  MODEL-INFORMED PRECISION DOSING
                </Text>
                <Title className="landing-hero-title" style={{ color: '#fff', fontSize: 48, lineHeight: 1.12, margin: '16px 0 24px', fontWeight: 700 }}>
                  {renderHeroTitle(hero.title)}
                </Title>
                <div
                  className="landing-hero-subtitle"
                  style={{ fontSize: 18, lineHeight: 1.75, maxWidth: 480 }}
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(hero.subtitle || '') }}
                />
                <Space size={16} className="landing-hero-actions">
                  {renderPrimaryAction(hero)}
                  {renderSecondaryAction()}
                </Space>
              </div>
            </div>

            <div className="landing-hero-visual-shell">
              <div className="landing-hero-visual-frame">
                <div className="landing-hero-visual-media-wrap">
                  {imageUrl ? (
                    <div
                      className="landing-hero-visual-plate"
                      role="img"
                      aria-label={hero.title}
                      style={{ backgroundImage: `url("${imageUrl}")` }}
                    />
                  ) : (
                    <div className="landing-hero-fallback-visual">
                      <div className="landing-hero-fallback-card">
                        <CalculatorOutlined />
                        <div className="landing-hero-fallback-label">MIPD / PopPK / Bayesian Estimation</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  if (loading || (!activeSlides.length && !fallbackHero)) {
    return (
      <>
        <style>{HERO_FLOATING_STYLES}</style>
        <section
          className="landing-hero-section"
          aria-busy="true"
          aria-label="Landing page content loading"
          style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 50%, ${NAVY_LIGHT} 100%)`,
            padding: '52px 0 74px', position: 'relative', overflow: 'hidden',
          }}
        >
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
            <div className="landing-hero-stage">
              <div className="landing-hero-copy-shell" aria-hidden="true">
                <div className="landing-hero-copy-inner">
                  <div className="landing-loading-line is-kicker" />
                  <div className="landing-loading-line is-title" />
                  <div className="landing-loading-line is-title is-short" />
                  <div className="landing-loading-line is-copy" />
                  <div className="landing-loading-line is-copy is-short" />
                </div>
              </div>
              <div className="landing-hero-visual-shell">
                <div className="landing-hero-visual-frame">
                  <div className="landing-hero-visual-media-wrap">
                    <div className="landing-hero-fallback-visual">
                      <div className="landing-hero-fallback-card">
                        <CalculatorOutlined />
                        <div className="landing-hero-fallback-label">MIPD / PopPK / Bayesian Estimation</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!activeSlides.length) {
    return (
      <>
        <style>{HERO_FLOATING_STYLES}</style>
        {renderHeroScene(fallbackHero)}
      </>
    );
  }

  return (
    <>
      <style>{HERO_FLOATING_STYLES}</style>
      <div className="landing-hero-carousel">
        <Carousel ref={carouselRef} autoplay autoplaySpeed={5000} effect="fade" dots={{ className: 'hero-dots' }}>
          {activeSlides.map(slide => (
          <div key={slide.id}>
            {renderHeroScene(slide, slide.image_url ? resolveImageUrl(slide.image_url) : undefined)}
          </div>
        ))}
        </Carousel>
        {activeSlides.length > 1 && (
          <>
            <button
              onClick={() => carouselRef.current?.prev()}
              className="landing-hero-arrow is-prev"
            >
              <LeftOutlined />
            </button>
            <button
              onClick={() => carouselRef.current?.next()}
              className="landing-hero-arrow is-next"
            >
              <RightOutlined />
            </button>
          </>
        )}
      </div>
    </>
  );
};

const NavBar: React.FC<{
  onLogin: () => void;
  onRegister: () => void;
  links?: Array<{ label?: string; href?: string; sort_order?: number }>;
  siteName?: string;
  logoUrl?: string;
}> = ({ onLogin, onRegister, links, siteName, logoUrl }) => {
  const navLinks = getSortedItems(links);
  const resolvedSiteName = unwrapSiteConfigValue<string>(siteName) || '';
  const resolvedLogoUrl = resolvePublicAssetUrl(unwrapSiteConfigValue<string>(logoUrl));

  return (
    <nav aria-label="Main navigation" className="landing-navbar">
      <div className="landing-nav-inner" style={{
        maxWidth: 1200, margin: '0 auto', padding: '0 24px',
      }}>
        <div className="landing-logo-block">
          {resolvedLogoUrl ? <img
              className="landing-brand-logo"
              src={resolvedLogoUrl}
              alt={resolvedSiteName}
              style={{ height: 48, maxWidth: 220, objectFit: 'contain', display: 'block' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            /> : null}
          <span style={{ display: resolvedLogoUrl ? 'none' : 'flex', alignItems: 'center', gap: 8 }}>
            <MedicineBoxOutlined style={{ fontSize: 28, color: NAVY }} />
            <span style={{ fontSize: 20, fontWeight: 700, color: NAVY, letterSpacing: -0.5 }}>
              {resolvedSiteName}
            </span>
          </span>
        </div>

        <div className="landing-nav-links">
          {navLinks.map((link, index) => (
            <a key={`${link.label || 'nav'}-${index}`} href={link.href || '#'}>{NAVIGATION_LABELS[link.label || ''] || link.label}</a>
          ))}
        </div>

        <Space size={12} className="landing-nav-actions">
          <Button className="landing-btn-secondary" icon={<LoginOutlined />} onClick={onLogin} style={{ color: NAVY }}>
            登录
          </Button>
          <Button className="landing-btn-primary" type="primary" icon={<UserAddOutlined />} onClick={onRegister}>
            免费注册
          </Button>
        </Space>
      </div>
    </nav>
  );
};

const TrustBar: React.FC<{ items?: any[] }> = ({ items }) => {
  const trustItems = getSortedItems(items);
  if (!trustItems.length) return null;

  return (
    <section className="landing-trust-section">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <Row className="landing-trust-grid" gutter={[20, 20]} justify="space-around">
          {trustItems.map((item, idx) => (
            <Col key={item.label || idx} xs={12} sm={6}>
              <div className="landing-trust-card">
                <div className="landing-trust-icon">{getIcon(item.icon, 22)}</div>
                <div className="landing-trust-label">{item.label}</div>
                <div className="landing-trust-desc">{item.desc || item.description || item.value}</div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

const FEATURE_DETAIL_META = [
  { phase: '模型匹配', input: '患者协变量与适用边界', output: '候选模型与选择依据', value: '在适用边界内选择更匹配的模型，减少经验式判断。' },
  { phase: '临床决策', input: '药物、目标与计算条件', output: '结构化建议与解释', value: '将复杂计算与推荐理由转化为清晰、可理解的临床信息。' },
  { phase: '结果复核', input: 'PK/PD 参数与给药方案', output: '浓度曲线与暴露指标', value: '同步查看暴露水平、浓度趋势和目标达成情况。' },
  { phase: '模型资产', input: '药物配置与模型版本', output: '可维护的模型能力', value: '让不同药物与人群模型以统一方式维护、验证和调用。' },
  { phase: 'TDM 反馈', input: '实测浓度与时间信息', output: '更新后的个体化方案', value: '利用实测浓度持续更新个体参数与后续给药方案。' },
  { phase: '安全治理', input: '角色、范围与审计记录', output: '可控的团队协作', value: '确保模型、配置和临床操作均可追踪、可审计。' },
];

const getFeatureMeta = (feature: any, index: number) => {
  const title = String(feature?.title || '').toLowerCase();
  if (title.includes('mipd') || title.includes('模型')) return FEATURE_DETAIL_META[0];
  if (title.includes('agent') || title.includes('智能助手')) return FEATURE_DETAIL_META[1];
  if (title.includes('pk') || title.includes('可视化')) return FEATURE_DETAIL_META[2];
  if (title.includes('多药') || title.includes('模型库')) return FEATURE_DETAIL_META[3];
  if (title.includes('tdm') || title.includes('闭环')) return FEATURE_DETAIL_META[4];
  if (title.includes('安全') || title.includes('租户') || title.includes('团队')) return FEATURE_DETAIL_META[5];
  return FEATURE_DETAIL_META[index] || {
    phase: '工作流能力',
    input: '患者信息与业务配置',
    output: '可复核的计算结果',
    value: '把关键输入、计算过程与输出结果连接成可复核的工作流。',
  };
};

export const Features: React.FC<{ features?: any[] }> = ({ features }) => {
  const featureList = getSortedItems(features);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  if (!featureList.length) return null;
  const activeFeature = featureList[activeFeatureIndex] || featureList[0];
  const activeMeta = getFeatureMeta(activeFeature, activeFeatureIndex);

  return (
    <Section id="features" bg="transparent" py={54} className="landing-section-features">
      <div className="landing-section-panel landing-feature-panel">
        <SectionTitle title="核心功能" subtitle="全方位覆盖精准给药工作流" />
        <div className="landing-feature-layout">
          <div className="landing-feature-index-list" role="tablist" aria-label="核心功能目录">
            {featureList.map((feature, idx) => (
              <button
                key={feature.id || idx}
                type="button"
                role="tab"
                aria-selected={activeFeatureIndex === idx}
                aria-controls={`landing-feature-panel-${idx}`}
                className={`landing-feature-index-row ${activeFeatureIndex === idx ? 'is-active' : ''}`}
                onClick={() => setActiveFeatureIndex(idx)}
              >
                <span className="landing-feature-index">{String(idx + 1).padStart(2, '0')}</span>
                <span className="landing-feature-index-icon">{getIcon(feature.icon, 18)}</span>
                <span className="landing-feature-index-title">{feature.title}</span>
                <span className="landing-feature-index-action">
                  {activeFeatureIndex === idx ? '查看中' : '查看'}
                  <ArrowRightOutlined className="landing-feature-index-arrow" />
                </span>
              </button>
            ))}
            <div className="landing-feature-index-footer">
              <span>能力导航</span>
              <p>选择一项能力，查看其临床输入与可复核输出。</p>
            </div>
          </div>
          <div
            id={`landing-feature-panel-${activeFeatureIndex}`}
            className="landing-feature-focus"
            role="tabpanel"
            aria-label={activeFeature?.title || '核心功能'}
          >
            <div className="landing-feature-focus-head">
              <div>
                <div className="landing-feature-focus-kicker">能力 {String(activeFeatureIndex + 1).padStart(2, '0')} / {String(featureList.length).padStart(2, '0')}</div>
                <Title level={3} className="landing-feature-focus-title">
                  {activeFeature?.title || '核心能力'}
                </Title>
              </div>
              <span className="landing-feature-focus-phase">{activeMeta.phase}</span>
            </div>
            <div
              className="landing-feature-focus-copy"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(activeFeature?.description || '') }}
            />
            <div className="landing-feature-value">
              <span>临床价值</span>
              <strong>{activeMeta.value}</strong>
            </div>
            <div className="landing-feature-detail-label">决策链路</div>
            <div className="landing-feature-flow" aria-label="能力在给药工作流中的位置">
              <span>患者信息</span>
              <ArrowRightOutlined />
              <span className="is-current">{activeMeta.phase}</span>
              <ArrowRightOutlined />
              <span>可复核结果</span>
            </div>
            <div className="landing-feature-detail-label is-evidence">结构化依据</div>
            <div className="landing-feature-evidence-grid">
              <div className="landing-feature-evidence-item">
                <span>输入依据</span>
                <strong>{activeMeta.input}</strong>
              </div>
              <div className="landing-feature-evidence-item">
                <span>输出结果</span>
                <strong>{activeMeta.output}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const HowItWorks: React.FC<{ steps?: any[] }> = ({ steps }) => {
  const workflowSteps = [...(steps || [])].sort((a, b) => {
    const aOrder = a.sort_order ?? a.step_number ?? a.step ?? 0;
    const bOrder = b.sort_order ?? b.step_number ?? b.step ?? 0;
    return aOrder - bOrder;
  });
  if (!workflowSteps.length) return null;

  return (
    <Section id="how-it-works" bg="transparent" py={42} className="landing-section-workflow">
      <div className="landing-section-panel landing-workflow-panel">
        <SectionTitle title="使用流程" subtitle="四步完成精准给药" />
        <div className="landing-workflow-grid">
          {workflowSteps.map((s, i) => {
            const stepNumber = String(s.step_number ?? s.step ?? i + 1).padStart(2, '0');

            return (
              <div key={s.id || i} className={`landing-step-shell ${i < workflowSteps.length - 1 ? 'has-connector' : ''}`}>
                <div className="landing-step-marker">{stepNumber}</div>
                <div className="landing-step-card">
                  <div className="landing-step-top">
                    <div className="landing-step-heading">
                      <Title level={4} className="landing-step-title" style={{ color: NAVY, marginBottom: 0, lineHeight: 1.3 }}>
                        {s.title}
                      </Title>
                    </div>
                  </div>
                  <div
                    className="landing-step-copy"
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(s.description || '') }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

const Resources: React.FC<{ resources?: any[] }> = ({ resources }) => {
  const resourceList = getSortedItems(resources);
  if (!resourceList.length) return null;

  return (
    <Section id="resources" bg="transparent" py={58} className="landing-section-resources">
      <div className="landing-section-panel landing-resource-panel">
        <SectionTitle title="资源中心" subtitle="助力您的精准医疗实践" />
        <Row gutter={[20, 20]}>
          {resourceList.map((resource, idx) => {
            const href = resource.link || '#';
            const external = isExternalHref(href);

            return (
              <Col key={resource.id || idx} xs={24} md={8}>
                <a
                  href={href}
                  className="landing-resource-link"
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                >
                  <Card hoverable className="landing-surface-card landing-resource-card" bodyStyle={{ padding: 28 }}>
                    <div className="landing-resource-header">
                      <span className="landing-resource-icon">{getIcon(resource.icon, 20)}</span>
                    </div>
                    <Title level={4} style={{ color: NAVY, marginBottom: 10, lineHeight: 1.3 }}>
                      {resource.title}
                    </Title>
                    <div
                      className="landing-resource-copy"
                      dangerouslySetInnerHTML={{ __html: markdownToHtml(resource.description || '') }}
                    />
                    <div className="landing-resource-arrow"><ArrowRightOutlined /></div>
                  </Card>
                </a>
              </Col>
            );
          })}
        </Row>
      </div>
    </Section>
  );
};

const TestimonialCard: React.FC<{ testimonial: any }> = ({ testimonial }) => (
  <div className="landing-testimonial-card">
    <div style={{ color: GOLD, fontSize: 30, lineHeight: 1, marginBottom: 14 }}>"</div>
    <div
      style={{ color: 'rgba(255,255,255,0.86)', fontSize: 15, lineHeight: 1.82, marginBottom: 18 }}
      dangerouslySetInnerHTML={{ __html: markdownToHtml(testimonial.quote || '') }}
    />
    <div style={{ marginTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
      {testimonial.avatar && <Avatar src={testimonial.avatar} size={46} />}
      <div>
        <Text style={{ color: '#fff', fontWeight: 600, display: 'block' }}>{testimonial.author}</Text>
        <Text style={{ color: 'rgba(255,255,255,0.54)', fontSize: 13 }}>{testimonial.role}</Text>
      </div>
    </div>
  </div>
);

const Testimonials: React.FC<{ testimonials?: any[] }> = ({ testimonials }) => {
  const sorted = getSortedItems(testimonials);
  if (!sorted.length) return null;

  return (
    <section className="landing-testimonials-section">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        <SectionTitle title="用户评价" subtitle="来自一线临床药师的反馈" light />
        <Carousel
          autoplay
          autoplaySpeed={4200}
          slidesToShow={3}
          slidesToScroll={1}
          infinite
          dots={{ className: 'hero-dots' }}
          arrows
          prevArrow={<button style={{ color: GOLD }}><LeftOutlined /></button>}
          nextArrow={<button style={{ color: GOLD }}><RightOutlined /></button>}
          responsive={[
            { breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } },
            { breakpoint: 1200, settings: { slidesToShow: 2, slidesToScroll: 1 } },
          ]}
        >
          {sorted.map((t, idx) => (
            <div key={t.id || idx} style={{ padding: '0 12px' }}>
              <TestimonialCard testimonial={t} />
            </div>
          ))}
        </Carousel>
      </div>
    </section>
  );
};

const CTASection: React.FC<{ onRegister: () => void }> = ({ onRegister }) => (
  <section className="landing-cta-section">
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
      <div className="landing-cta-panel">
        <Row gutter={[24, 24]} align="middle" justify="space-between">
          <Col xs={24} md={15}>
            <Title level={2} style={{ color: '#FFFFFF', marginBottom: 14 }}>
              让每一次给药决策，都有据可查、可追溯
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.76)', fontSize: 16, lineHeight: 1.8, marginBottom: 0, maxWidth: 640 }}>
              从适用模型筛选、初始方案计算到 TDM 后续优化，让关键判断有据可查、便于复核。
            </Paragraph>
          </Col>
          <Col xs={24} md={9}>
            <Space size={14} className="landing-cta-actions" wrap>
              <Button className="landing-btn-primary" type="primary" size="large" icon={<UserAddOutlined />} onClick={onRegister}>
                免费注册
              </Button>
              <Button className="landing-btn-secondary" size="large" href="#features">
                了解功能
              </Button>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  </section>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate('/login');
  const goRegister = () => navigate('/register');

  const [content, setContent] = useState<any>(null);
  const [contentLoading, setContentLoading] = useState(true);
  const { landingHeaderLogoUrl, loading: brandingLoading } = useSiteBranding();

  useEffect(() => {
    let active = true;
    siteAPI.getLandingContent()
      .then((result) => {
        if (active) setContent(result || {});
      })
      .catch((error) => {
        console.error('Failed to load landing content:', error);
      })
      .finally(() => {
        if (active) setContentLoading(false);
      });
    return () => { active = false; };
  }, []);

  const loading = contentLoading || brandingLoading;

  if (loading) {
    return (
      <div className="landing-page">
        <style>{LANDING_PAGE_STYLES}</style>
        <main id="main-content" aria-busy="true">
          <HeroCarousel slides={[]} onRegister={goRegister} loading />
        </main>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <style>{LANDING_PAGE_STYLES}</style>
      <a href="#main-content" className="skip-nav">跳过导航</a>
      <NavBar
        onLogin={goLogin}
        onRegister={goRegister}
        links={content?.navbar_links}
        siteName={content?.site_name}
        logoUrl={landingHeaderLogoUrl || content?.site_logo}
      />
      <main id="main-content">
        <HeroCarousel
          slides={content?.landing_hero_slides || content?.landing_banners || []}
          fallbackHero={content?.landing_hero}
          onRegister={goRegister}
        />
        <TrustBar items={content?.landing_trust_bar} />
        <Features features={content?.landing_features} />
        <HowItWorks steps={content?.landing_workflow} />
        <Resources resources={content?.landing_resources} />
        <Testimonials testimonials={content?.landing_testimonials} />
        <CTASection onRegister={goRegister} />
      </main>
      <PageFooter />
    </div>
  );
};

export default LandingPage;
